import { gql, type ApolloCache } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";

import { useAuth } from "auth";

import type { SiteLanguage } from "./provider-site";
import { hasAiDesigner, hasOwnAssistant, hasProviderSite } from "./site-assistant";
import {
  APPLY_READY_DESIGN_MUTATION,
  DISABLE_MUTATION,
  DISCARD_DRAFT_MUTATION,
  ENABLE_MUTATION,
  MY_PROVIDER_SITE_QUERY,
  MY_SITE_ASSISTANT_QUERY,
  PUBLISH_MUTATION,
  READY_DESIGNS_QUERY,
  RESTORE_VERSION_MUTATION,
  SEND_MESSAGE_MUTATION,
  SET_LANGUAGE_MUTATION,
  SITE_ASSISTANT_ANSWER_MUTATION,
  SITE_ASSISTANT_CREATE_PAIRING_MUTATION,
  SITE_ASSISTANT_DISCONNECT_MUTATION,
  SITE_ENTITLEMENTS_QUERY,
  TRY_READY_DESIGN_MUTATION,
  type ProviderSite,
  type ReadyDesigns,
  type SiteAssistant,
} from "./site.documents";

const MY_PROVIDER_SITE = gql(MY_PROVIDER_SITE_QUERY);
const READY_DESIGNS = gql(READY_DESIGNS_QUERY);
const SITE_ENTITLEMENTS = gql(SITE_ENTITLEMENTS_QUERY);
const MY_SITE_ASSISTANT = gql(MY_SITE_ASSISTANT_QUERY);
const APPLY_READY_DESIGN_DOC = gql(APPLY_READY_DESIGN_MUTATION);
const DISABLE_DOC = gql(DISABLE_MUTATION);
const DISCARD_DRAFT_DOC = gql(DISCARD_DRAFT_MUTATION);
const ENABLE_DOC = gql(ENABLE_MUTATION);
const PUBLISH_DOC = gql(PUBLISH_MUTATION);
const RESTORE_VERSION_DOC = gql(RESTORE_VERSION_MUTATION);
const SEND_MESSAGE_DOC = gql(SEND_MESSAGE_MUTATION);
const SET_LANGUAGE_DOC = gql(SET_LANGUAGE_MUTATION);
const SITE_ASSISTANT_ANSWER_DOC = gql(SITE_ASSISTANT_ANSWER_MUTATION);
const SITE_ASSISTANT_CREATE_PAIRING_DOC = gql(SITE_ASSISTANT_CREATE_PAIRING_MUTATION);
const SITE_ASSISTANT_DISCONNECT_DOC = gql(SITE_ASSISTANT_DISCONNECT_MUTATION);
const TRY_READY_DESIGN_DOC = gql(TRY_READY_DESIGN_MUTATION);

type ProfileVariables = { profileUuid: string };

// Whether the Account may have a site at all, and whether it has Matcha's AI designer.
export function useSiteEntitlements() {
  const { isAuthenticated } = useAuth();
  const { data, loading } = useQuery<{
    mySubscription: { entitlements: { key: string; booleanValue?: boolean | null }[] };
  }>(SITE_ENTITLEMENTS, {
    skip: !isAuthenticated,
    fetchPolicy: "cache-and-network",
  });
  const entitlements = data?.mySubscription.entitlements;
  return {
    isLoading: loading && !data,
    isSiteAvailable: hasProviderSite(entitlements),
    aiDesigner: hasAiDesigner(entitlements),
    ownAssistant: hasOwnAssistant(entitlements),
  };
}

export function useMyProviderSite(profileUuid: string | null) {
  return useQuery<{ myProviderSite: ProviderSite | null }, ProfileVariables>(MY_PROVIDER_SITE, {
    variables: { profileUuid: profileUuid ?? "" },
    skip: !profileUuid,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });
}

export function useReadyDesigns(profileUuid: string | null) {
  return useQuery<{ readyDesigns: ReadyDesigns }, ProfileVariables>(READY_DESIGNS, {
    variables: { profileUuid: profileUuid ?? "" },
    skip: !profileUuid,
    fetchPolicy: "cache-and-network",
  });
}

// The first message or Ready design may create the site, which the "Мій сайт" query (null until
// then) does not know by itself, and discarding drops the draft card at once: these answers are
// written into the query.
function writeSite(
  cache: ApolloCache,
  profileUuid: string | undefined,
  site: ProviderSite | undefined
) {
  if (!profileUuid || !site) return;
  cache.writeQuery({
    query: MY_PROVIDER_SITE,
    variables: { profileUuid },
    data: { myProviderSite: site },
  });
}

export const useSendSiteMessage = () =>
  useMutation<{ providerSiteSendMessage: ProviderSite }, ProfileVariables & { text: string }>(
    SEND_MESSAGE_DOC,
    {
      update(cache, { data }, { variables }) {
        writeSite(cache, variables?.profileUuid, data?.providerSiteSendMessage);
      },
    }
  );

export const usePublishSite = () =>
  useMutation<{ providerSitePublish: ProviderSite }, ProfileVariables>(PUBLISH_DOC);

export const useRestoreSiteVersion = () =>
  useMutation<{ providerSiteRestoreVersion: ProviderSite }, ProfileVariables & { number: number }>(
    RESTORE_VERSION_DOC
  );

export const useDisableSite = () =>
  useMutation<{ providerSiteDisable: ProviderSite }, ProfileVariables>(DISABLE_DOC);

export const useEnableSite = () =>
  useMutation<{ providerSiteEnable: ProviderSite }, ProfileVariables>(ENABLE_DOC);

export const useSetSiteLanguage = () =>
  useMutation<{ providerSiteSetLanguage: ProviderSite }, ProfileVariables & { lang: SiteLanguage }>(
    SET_LANGUAGE_DOC
  );

export const useApplyReadyDesign = () =>
  useMutation<{ providerSiteApplyReadyDesign: ProviderSite }, ProfileVariables & { id: string }>(
    APPLY_READY_DESIGN_DOC,
    {
      update(cache, { data }, { variables }) {
        writeSite(cache, variables?.profileUuid, data?.providerSiteApplyReadyDesign);
      },
    }
  );

export const useDiscardSiteDraft = () =>
  useMutation<{ providerSiteDiscardDraft: ProviderSite }, ProfileVariables>(DISCARD_DRAFT_DOC, {
    update(cache, { data }, { variables }) {
      writeSite(cache, variables?.profileUuid, data?.providerSiteDiscardDraft);
    },
  });

export const useTryReadyDesign = () =>
  useMutation<
    { providerSiteTryReadyDesign: { id: string; previewUrl: string } },
    ProfileVariables & { id: string }
  >(TRY_READY_DESIGN_DOC);

export function useMySiteAssistant({ skip = false }: { skip?: boolean } = {}) {
  return useQuery<{ mySiteAssistant: SiteAssistant }>(MY_SITE_ASSISTANT, {
    skip,
    fetchPolicy: "cache-and-network",
  });
}

export const useCreateSiteAssistantPairing = () =>
  useMutation<{ siteAssistantCreatePairing: { installUrl: string; expiresAt: string } }>(
    SITE_ASSISTANT_CREATE_PAIRING_DOC
  );

export const useAnswerSiteAssistant = () =>
  useMutation<{ siteAssistantAnswer: boolean }, { requestUuid: string; allow: boolean }>(
    SITE_ASSISTANT_ANSWER_DOC,
    { refetchQueries: ["MySiteAssistant"] }
  );

export const useDisconnectSiteAssistant = () =>
  useMutation<{ siteAssistantDisconnect: boolean }>(SITE_ASSISTANT_DISCONNECT_DOC, {
    refetchQueries: ["MySiteAssistant"],
  });
